import { AviosCurrencySymbol, Box, IconButton } from '@alto-avios/alto-ui';
import { FC } from 'react';
import { useVariableValue } from '@devcycle/react-client-sdk';

import { useUserDetailsError } from '../../contexts/userDetailsError';
import { isNonNegativeNumber } from '../../utilities';
import { AuthProps } from '../Header';
import { ManageAvios } from '../ManageAvios';
import { Popover } from '../Popover';
import { HeaderContent, useRequiredContent } from '../services/content';

import styles from './MyAccountDetails.module.css';

export interface MyAccountProps {
  balance?: number;
  householdAviosBalance?: number;
  name: string;
  logoutProps: AuthProps['logoutProps'];
}

export const MyAccountDetails: FC<MyAccountProps> = ({ balance, householdAviosBalance, name, logoutProps }) => {
  const showHouseholdBalance = useVariableValue('show-household-balance', false);
  const {
    data: {
      account: { triggerAriaLabel, overlayAriaLabel },
    },
  } = useRequiredContent(HeaderContent);
  const { userDetailsError, userBalanceError } = useUserDetailsError();

  const renderBalance = (personalAviosBalance?: number, householdAviosBalance?: number) => {
    if (showHouseholdBalance && isNonNegativeNumber(householdAviosBalance)) {
      return householdAviosBalance?.toLocaleString('en-GB');
    }
    return isNonNegativeNumber(personalAviosBalance) && personalAviosBalance?.toLocaleString('en-GB');
  };

  const userIcon = (
    <IconButton
      aria-label={triggerAriaLabel}
      data-testid="userIcon"
      emphasis="quaternary"
      iconProps={{ iconName: 'user', iconSize: '2x', iconPrefix: 'far' }}
      styleVariant="neutral"
    />
  );

  return (
    <Box alignItems="center" borderWidth="none" data-testid="my-account" gap="xs">
      {!userDetailsError && (
        <Box alignItems="flexEnd" borderWidth="none" flexDirection="column">
          <p className={styles.name}>{name}</p>
          {!userBalanceError && (
            <Box alignItems="center" borderWidth="none" gap="5xs">
              <AviosCurrencySymbol kind="balance" styleVariant="accentSecondary" width="12px" />
              <p className={styles.aviosBalance}>{renderBalance(balance, householdAviosBalance)}</p>
            </Box>
          )}
        </Box>
      )}
      <Popover placement="bottom right" closeDelay={600} openDelay={300} trigger={userIcon} aria-label={overlayAriaLabel} isNonModal>
        <ManageAvios firstName={name} householdAviosBalance={householdAviosBalance} logoutProps={logoutProps} personalAviosBalance={balance} />
      </Popover>
    </Box>
  );
};
